import React, { useContext } from 'react';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import { colors, shadow, zIndexes } from 'src/styling/constants';
import mq from 'src/styling/media-queries';
import type { CSSInterpolation } from '@emotion/serialize';
import { WebsiteLink } from 'src/components/common/website-link';

type LinkDropdownProps = {
  links: readonly Queries.LinkDropdownCtaDropdownOptions[];
  showingMobileNav?: boolean;
  onClick: () => void;
};

const createStyling = (styling: Styling, showingMobileNav?: boolean) =>
  ({
    dropdownWrapper: { zIndex: zIndexes.popup, top: '0' },
    dropdownContainer: mq.withSmallTablet([
      shadow.below,
      {
        backgroundColor: styling.colors.cream,
        width: showingMobileNav ? '100vw' : 'auto',
        minWidth: [
          '100vw',
          showingMobileNav ? undefined : '20rem',
          showingMobileNav ? undefined : '20rem',
        ],
        right: ['-0.5rem', 0, 0],
        position: 'absolute',
        top: showingMobileNav ? 'unset' : '1px',
        bottom: showingMobileNav ? 'calc(3rem + 1px)' : 'unset',
        ...(showingMobileNav ? { maxWidth: '30rem' } : {}),
      },
    ]),
    link: [
      styling.fonts.regular,
      {
        'padding': '0 2rem',
        'display': 'flex',
        'alignItems': 'center',
        'fontSize': '1.1875rem',
        'lineHeight': '145%',
        'minHeight': '4rem',
        'borderBottom': `1px solid ${colors.transparentGrayOlive}`,
        '&:last-child': {
          borderBottom: 'none',
        },
        '&: hover': {
          textDecoration: 'underline',
          color: styling.colors.camelot,
        },
      },
    ],
  }) satisfies Record<string, CSSInterpolation>;

const LinkDropdown = ({ links, showingMobileNav, onClick }: LinkDropdownProps) => {
  const { styling } = useContext(SiteContext);
  const componentStyling = createStyling(styling, showingMobileNav);

  return (
    <div className="relative" css={componentStyling.dropdownWrapper}>
      <div css={componentStyling.dropdownContainer}>
        <div className="flex flex-column">
          {links.map(({ link, displayText }, index) => (
            <WebsiteLink to={link} css={componentStyling.link} key={index} handleClick={onClick}>
              {displayText}
            </WebsiteLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LinkDropdown;
